"use client";
import React, { useState } from "react";

type BillingCycle = "monthly" | "yearly";
import type { PricingPlan } from "@/types/sections/pricing";
import type { Props } from "@/components/pricing";
import { paddingMap } from "@/components/pricing";
import { getBackgroundStyle, getBtnVariant } from "@/lib/background";
import { Button } from "@/components/ui/button";

export default function PricingSwitcher({ data }: Props) {
  const {
    title,
    subtitle,
    toggle = { enabled: true, labels: ["Monthly", "Yearly"] },
    plans = [],
    background,
    padding = "xl",
  } = data || {};

  const [billingCycle, setBillingCycle] = useState<BillingCycle>("monthly");

  const bgStyle = getBackgroundStyle(background);

  return (
    <section
      className={`${paddingMap[padding] || "py-20 px-6"}`}
      style={bgStyle}
    >
      <div className="max-w-7xl mx-auto text-center">
        {title && (
          <h2 className="text-4xl font-bold mb-4 text-foreground">{title}</h2>
        )}
        {subtitle && (
          <p className="text-lg text-muted-foreground mb-6">{subtitle}</p>
        )}

        {toggle.enabled && (
          <div className="mb-10 flex justify-center items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <span>{toggle.labels?.[0] || "Monthly"}</span>
              <div className="relative inline-flex items-center">
                <input
                  type="checkbox"
                  checked={billingCycle === "yearly"}
                  onChange={() =>
                    setBillingCycle((prev) =>
                      prev === "monthly" ? "yearly" : "monthly",
                    )
                  }
                  className="sr-only"
                />
                <div className="w-12 h-6 bg-gray-300 rounded-full shadow-inner" />
                <div
                  className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transform transition-transform ${
                    billingCycle === "yearly" ? "translate-x-6" : ""
                  }`}
                />
              </div>
              <span>{toggle.labels?.[1] || "Yearly"}</span>
            </label>
          </div>
        )}

        <div className="grid gap-6 sm:grid-cols-2 md:grid-cols-3">
          {plans.map((plan, idx) => (
            <div
              key={idx}
              className={`border rounded-lg p-6 shadow-sm ${
                plan.highlighted
                  ? "border-primary bg-primary/5"
                  : "border-gray-200 bg-white"
              }`}
            >
              <h3 className="text-xl font-semibold mb-1 text-foreground">
                {plan.name}
              </h3>
              <p className="mb-4 text-muted-foreground">{plan.description}</p>
              <div className="text-3xl font-bold mb-4">
                {plan.currency || "$"}
                {plan.price?.[billingCycle] ?? 0}
                <span className="text-sm text-muted-foreground">
                  {" "}
                  /{billingCycle === "yearly" ? "yr" : "mo"}
                </span>
              </div>
              <ul className="mb-6 text-left text-sm space-y-2 text-foreground">
                {plan.features?.map((feature, i) => (
                  <li key={i}>• {feature}</li>
                ))}
              </ul>
              {plan.button?.href && plan.button?.text && (
                <Button
                  asChild
                  variant={getBtnVariant(plan.button.style)} // dùng mặc định nếu thiếu
                  className="px-5 py-2 text-sm font-medium"
                >
                  <a href={plan.button.href}>{plan.button.text}</a>
                </Button>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
